<template>
    <div>
        <div class="h-24 flex flex-col">
            <div class="setting-header">
                Elektronisk Handelsformat
            </div>
            <div class="setting-group">
                <div class="setting">
                    <div class="flex justify-between items-center">
                        Kan foretaket motta EHF?
                        <div v-if="isLoading">
                            <i class="i-ph-spinner animate-spin text-primary text-2xl inline-block"/>
                        </div>
                        <div v-else-if="isPeppolRegistered?.canReceiveInvoices" class="flex items-center text-2xl gap-2">
                            Ja
                            <i class="i-ph-check-bold inline-block text-primary text-3xl"/>
                        </div>
                        <div v-else class="flex items-center text-2xl gap-2">
                            Nei
                            <i class="i-ph-x inline-block text-error text-3xl"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <UButton
            color="neutral"
            variant="subtle"
            class="mt-6 mr-2"
            @click="refreshPeppol"
        >
            <i class="i-ph-arrows-clockwise"/>
        </UButton>
        <UButton class="mt-6 mb-2" :disabled="isLoading || isInvalidRegistration" @click="toggleParticipation">
            {{ isPeppolRegistered?.canReceiveInvoices ? 'Avregistrer' : 'Registrér virksomhet i EHF-nettverket' }}
        </UButton>
        <div v-if="isInvalidRegistration" class="text-error m-2 p-2 bg-base-200">
            Mangler Kontaktinformasjon:
            <template v-if="!organization?.contactInfo?.email">
                <br>
                - E-post
            </template>
            <template v-if="!organization?.contactInfo?.phoneNumber">
                <br>
                - Telefonnummer
            </template>
            <br>
            Gå til <a class="text-primary underline" :href="`${patronUrl}/${selectedOrg?.organizationId}`">start</a> og registrer dette på virksomheten.
        </div>
        <div v-if="ehfRegistrationStatus">
            <pre>{{ ehfRegistrationStatus }}</pre>
        </div>
    </div>
</template>

<script setup lang="ts">

import {deletePeppolParticipant, getPeppolParticipation, registerPeppolParticipant} from '@/invoice/invoice.service'
import {computed, onMounted, onUnmounted, ref} from 'vue'
import {orgSelectionComplete, selectedOrg} from '@/tenants/org.service'
import {useEmitter} from '@/services/infrastructure/emitter'
import {ORG_SWITCHED} from '@/types/events'
import {organization} from '@/organization/organization.service'
import {patronUrl} from '@/services/infrastructure/patron.service'
import {refreshSetting} from '@/setting/setting.service'

const emitter = useEmitter()
const isPeppolRegistered = ref()
const isLoading = ref(false)
const ehfRegistrationStatus = ref('')
const refreshPeppol = async () => {
    isLoading.value = true
    isPeppolRegistered.value = null
    await orgSelectionComplete
    try {
        isPeppolRegistered.value = await getPeppolParticipation()
    } catch (e) {
        console.warn('Unable to fetch Peppol info. Failed with error ', e)
    } finally {
        isLoading.value = false
    }
}

// TODO check if registered with pracsys or other system
const isInvalidRegistration = computed<boolean>(() => !organization.value?.contactInfo?.email || !organization.value?.contactInfo?.phoneNumber)

const toggleParticipation = async () => {
    isLoading.value = true
    if (isPeppolRegistered.value?.canReceiveInvoices) {
        try {
            ehfRegistrationStatus.value = await deletePeppolParticipant()
        } finally {
            isLoading.value = false
        }
    } else {
        try {
            ehfRegistrationStatus.value = await registerPeppolParticipant()
        } catch (e) {
            // @ts-ignore
            ehfRegistrationStatus.value = e?.response?.data?.detail || e
        } finally {
            isLoading.value = false
        }
    }
    await refreshPeppol()
    await refreshSetting('REGISTERED_EHF_RECIPIENT')
}

const refresh = async () => {
    await refreshPeppol()
    ehfRegistrationStatus.value = ''
}

onMounted(async () => {
    await refresh()
    emitter.onEvent(ORG_SWITCHED, refresh)
})

onUnmounted(emitter.cleanUp)

</script>

<style scoped>

</style>